import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../App';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Globe, AlertTriangle, User, Mail, MapPin, Link as LinkIcon, Clock, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { appealsAPI } from '../lib/api';
import { toast } from 'sonner';

const STATUS_MAP = {
  pending_payment: { dark: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', light: 'bg-yellow-50 text-yellow-600 border-yellow-200', label: 'Pending Payment' },
  submitted: { dark: 'bg-blue-500/10 text-blue-400 border-blue-500/20', light: 'bg-blue-50 text-blue-600 border-blue-200', label: 'Submitted' },
  in_review: { dark: 'bg-purple-500/10 text-purple-400 border-purple-500/20', light: 'bg-purple-50 text-purple-600 border-purple-200', label: 'In Review' },
  approved: { dark: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', light: 'bg-emerald-50 text-emerald-600 border-emerald-200', label: 'Approved' },
  rejected: { dark: 'bg-red-500/10 text-red-400 border-red-500/20', light: 'bg-red-50 text-red-600 border-red-200', label: 'Rejected' },
  completed: { dark: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', light: 'bg-cyan-50 text-cyan-600 border-cyan-200', label: 'Completed' },
};

const strings = {
  en: {
    back: 'Back',
    appealDetail: 'Appeal Details',
    trackingCode: 'Tracking Code',
    platform: 'Platform',
    issueType: 'Issue Type',
    description: 'Description',
    fullName: 'Full Name',
    email: 'Email',
    country: 'Country',
    evidence: 'Evidence URLs',
    customFields: 'Additional Information',
    timeline: 'Timeline',
    notes: 'Notes & Comments',
    addNote: 'Add Note',
    notePlaceholder: 'Write your note...',
    submitNote: 'Post Note',
    noNotes: 'No notes yet',
    submittedBy: 'Submitted by',
    status: 'Status',
    created: 'Created',
    updated: 'Last Updated',
    notFound: 'Appeal not found',
  },
  ar: {
    back: 'رجوع',
    appealDetail: 'تفاصيل الطعن',
    trackingCode: 'رمز التتبع',
    platform: 'المنصة',
    issueType: 'نوع المشكلة',
    description: 'الوصف',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    country: 'الدولة',
    evidence: 'روابط الأدلة',
    customFields: 'معلومات إضافية',
    timeline: 'الجدول الزمني',
    notes: 'الملاحظات والتعليقات',
    addNote: 'إضافة ملاحظة',
    notePlaceholder: 'اكتب ملاحظتك...',
    submitNote: 'نشر الملاحظة',
    noNotes: 'لا توجد ملاحظات بعد',
    submittedBy: 'مقدم من',
    status: 'الحالة',
    created: 'تاريخ الإنشاء',
    updated: 'آخر تحديث',
    notFound: 'الطعن غير موجود',
  },
  de: {
    back: 'Zurück',
    appealDetail: 'Beschwerdedetails',
    trackingCode: 'Tracking-Code',
    platform: 'Plattform',
    issueType: 'Problemtyp',
    description: 'Beschreibung',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    country: 'Land',
    evidence: 'Beweislinks',
    customFields: 'Zusätzliche Informationen',
    timeline: 'Zeitverlauf',
    notes: 'Notizen und Kommentare',
    addNote: 'Notiz hinzufügen',
    notePlaceholder: 'Schreiben Sie Ihre Notiz...',
    submitNote: 'Notiz posten',
    noNotes: 'Noch keine Notizen',
    submittedBy: 'Eingereicht von',
    status: 'Status',
    created: 'Erstellt',
    updated: 'Zuletzt aktualisiert',
    notFound: 'Beschwerde nicht gefunden',
  },
};

export default function AppealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { i18n } = useTranslation();
  const lang = ['en','ar','de'].includes(i18n.language) ? i18n.language : 'en';
  const s = strings[lang];

  const [appeal, setAppeal] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [addingNote, setAddingNote] = useState(false);

  const card = isDark
    ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]'
    : 'bg-white border border-gray-200 shadow-sm';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-600';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const borderCl = isDark ? 'border-white/[0.05]' : 'border-gray-100';
  const labelCl = `text-xs font-medium uppercase tracking-wide block mb-1.5 ${textM}`;

  useEffect(() => {
    loadAppeal();
  }, [id]);

  const loadAppeal = async () => {
    try {
      const res = await appealsAPI.get(id);
      setAppeal(res.data);
      setNotes(res.data.notes_list || []);
    } catch {
      toast.error(s.notFound);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await appealsAPI.addNote(id, noteText.trim());
      setNoteText('');
      toast.success('Note added');
      loadAppeal();
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusClass = (status) => {
    const map = STATUS_MAP[status];
    if (!map) return '';
    return `${isDark ? map.dark : map.light} border`;
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
        <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!appeal) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
        <FileText className={`w-16 h-16 mb-4 ${textM}`} />
        <p className={textS}>{s.notFound}</p>
        <Button onClick={() => navigate(-1)} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl">
          <ArrowLeft className="w-4 h-4 mr-2" />{s.back}
        </Button>
      </div>
    );
  }

  const statusInfo = STATUS_MAP[appeal.status] || {};

  const fields = [
    { icon: Globe, label: s.platform, value: appeal.platform },
    { icon: AlertTriangle, label: s.issueType, value: appeal.issue_type },
    { icon: User, label: s.fullName, value: appeal.full_name },
    { icon: Mail, label: s.email, value: appeal.email },
    { icon: MapPin, label: s.country, value: appeal.country || '—' },
  ];

  return (
    <div className={`min-h-screen transition-colors py-8 px-6 ${isDark ? '' : 'bg-[#F8FAFC]'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className={`rounded-xl ${isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
            <ArrowLeft className="w-4 h-4 mr-2" />{s.back}
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.appealDetail}</h1>
            <div className={`flex items-center gap-2 mt-1 text-sm ${textM}`}>
              <span className="font-mono">{appeal.tracking_code}</span>
              <span>·</span>
              <span>{new Date(appeal.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusClass(appeal.status)}`}>
            {statusInfo.label || appeal.status}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Fields Card */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className={`${card} rounded-2xl p-6`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {fields.map((f, i) => (
                  <div key={i}>
                    <label className={labelCl}>{f.label}</label>
                    <div className={`flex items-center gap-2 text-sm ${textP}`}>
                      <f.icon className={`w-4 h-4 ${textM} flex-shrink-0`} />
                      <span>{f.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={`${card} rounded-2xl p-6`}>
              <label className={labelCl}>{s.description}</label>
              <p className={`text-sm leading-relaxed whitespace-pre-wrap ${textS}`}>{appeal.description}</p>
            </motion.div>

            {/* Evidence URLs */}
            {appeal.evidence_urls && appeal.evidence_urls.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={`${card} rounded-2xl p-6`}>
                <label className={labelCl}>{s.evidence}</label>
                <div className="space-y-2">
                  {appeal.evidence_urls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 text-sm text-blue-500 hover:text-blue-400 transition-colors`}>
                      <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{url}</span>
                    </a>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Custom Fields */}
            {appeal.custom_fields && Object.keys(appeal.custom_fields).length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={`${card} rounded-2xl p-6`}>
                <label className={labelCl}>{s.customFields}</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  {Object.entries(appeal.custom_fields).map(([k, v]) => (
                    <div key={k}>
                      <div className={`text-xs capitalize ${textM} mb-0.5`}>{k.replace(/_/g, ' ')}</div>
                      <div className={`text-sm ${textP}`}>{v}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Notes */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className={`${card} rounded-2xl p-6`}>
              <h3 className={`font-semibold mb-4 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.notes}</h3>
              <div className="space-y-3 mb-4">
                {notes.length === 0 ? (
                  <div className={`text-sm text-center py-6 ${textM}`}>{s.noNotes}</div>
                ) : (
                  notes.map((n, i) => (
                    <div key={n.id || i} className={`p-4 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/[0.05]' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className={`flex items-center gap-2 mb-2 text-xs ${textM}`}>
                        <span className="font-medium">{n.author_email}</span>
                        <span>·</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          n.author_role === 'super_admin' ? (isDark ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600') :
                          n.author_role === 'authority' ? (isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600') :
                          (isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-500')
                        }`}>{n.author_role}</span>
                        <span>·</span>
                        <span>{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <p className={`text-sm ${textS}`}>{n.content}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                  placeholder={s.notePlaceholder}
                  className={`${inputCl} rounded-xl h-10`}
                />
                <Button onClick={handleAddNote} disabled={addingNote || !noteText.trim()} className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-4 flex-shrink-0">
                  <MessageSquare className="w-4 h-4 mr-1" />{s.submitNote}
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Status */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={`${card} rounded-2xl p-5`}>
              <label className={labelCl}>{s.status}</label>
              <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium ${getStatusClass(appeal.status)}`}>
                {statusInfo.label || appeal.status}
              </span>
              <div className={`mt-4 pt-4 border-t ${borderCl} space-y-3`}>
                <div>
                  <div className={`text-xs ${textM} mb-0.5`}>{s.created}</div>
                  <div className={`text-sm ${textS}`}>{new Date(appeal.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className={`text-xs ${textM} mb-0.5`}>{s.updated}</div>
                  <div className={`text-sm ${textS}`}>{new Date(appeal.updated_at).toLocaleString()}</div>
                </div>
              </div>
            </motion.div>

            {/* Timeline */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={`${card} rounded-2xl p-5`}>
              <h3 className={`font-semibold mb-4 ${textP}`} style={{ fontFamily: 'Outfit' }}>{s.timeline}</h3>
              <div className="relative">
                <div className={`absolute left-3.5 top-5 bottom-5 w-px ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`} />
                <div className="space-y-4">
                  {/* Created event */}
                  <div className="flex items-start gap-3 relative">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${isDark ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                      <Clock className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    <div className="pt-0.5">
                      <div className={`text-xs font-medium ${textP}`}>Appeal Created</div>
                      <div className={`text-xs ${textM}`}>{new Date(appeal.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  {/* Notes as timeline */}
                  {notes.slice().reverse().map((n, i) => (
                    <div key={n.id || i} className="flex items-start gap-3 relative">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${isDark ? 'bg-white/[0.05] border border-white/[0.08]' : 'bg-gray-50 border border-gray-200'}`}>
                        <MessageSquare className={`w-3.5 h-3.5 ${textM}`} />
                      </div>
                      <div className="pt-0.5 flex-1 min-w-0">
                        <div className={`text-xs font-medium ${textP}`}>{n.author_role}</div>
                        <div className={`text-xs ${textM} mb-0.5`}>{new Date(n.created_at).toLocaleString()}</div>
                        <p className={`text-xs truncate ${textS}`}>{n.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
